import { computed, inject, provide, type InjectionKey, type Ref } from 'vue'

import type { DistributorApplicationDetails, KalpCalculation } from '@/services/application'
import type { DistributorCommitmentDetails } from '@/services/commitment'

export type ApplicationContext = {
  application: Ref<DistributorApplicationDetails | DistributorCommitmentDetails | null>
  isLoading: Ref<boolean>
  error: Ref<string>
}

const APPLICATION_CONTEXT_KEY = Symbol() as InjectionKey<ApplicationContext>

export const provideApplicationContext = (context: ApplicationContext) => {
  provide(APPLICATION_CONTEXT_KEY, context)
}

// Throws early if called outside that provider to avoid silent undefined behavior.
// Union-typed: for the few components rendered under BOTH layouts.
export const useApplicationRecord = () => {
  const context = inject(APPLICATION_CONTEXT_KEY)

  if (!context) {
    throw new Error('useApplicationRecord must be used within an application layout.')
  }

  return context
}

export const useApplicationContext = () => {
  const context = useApplicationRecord()

  return context as ApplicationContext & { application: Ref<DistributorApplicationDetails | null> }
}

// Returns a non-null application ref for sections that only render after loading is complete.
// Throws if the application is still null so consumers can skip repetitive non-null assertions.
export const useRequiredApplication = () => {
  const { application } = useApplicationContext()

  return computed(() => {
    if (!application.value) {
      throw new Error('useRequiredApplication must be used when application is loaded.')
    }

    return application.value as DistributorApplicationDetails
  })
}

export const useRequiredCommitment = () => {
  const { application } = useApplicationRecord()

  return computed(() => {
    if (!application.value) {
      throw new Error('useRequiredCommitment must be used when the commitment is loaded.')
    }

    return application.value as DistributorCommitmentDetails
  })
}

export type ApplicationKalpContext = {
  kalp: Ref<KalpCalculation | null>
  isKalpLoading: Ref<boolean>
  kalpError: Ref<string>
}

const APPLICATION_KALP_CONTEXT_KEY = Symbol() as InjectionKey<ApplicationKalpContext>

export const provideApplicationKalpContext = (context: ApplicationKalpContext) => {
  provide(APPLICATION_KALP_CONTEXT_KEY, context)
}

export const useApplicationKalpContext = () => {
  const context = inject(APPLICATION_KALP_CONTEXT_KEY)
  if (!context) {
    throw new Error('useApplicationKalpContext must be used within ApplicationLayout.')
  }
  return context
}
